import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2, Download } from "lucide-react";
import {
  templatePushSitesApi,
  mainTemplatesApi,
  pushSitesApi,
  protocolsApi,
  ApiError,
  type MainTemplate,
  type MainProtocol,
  type PushSite,
  type TemplatePushSite,
  type PushSiteTemplate,
} from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FieldMappingEditor } from "@/components/FieldMappingEditor";
import { TableShell, type ColumnSpec } from "@/components/TableShell";
import { InheritedModelConfigEditor } from "@/components/InheritedModelConfigEditor";
import { PushBindingEditModal } from "@/components/PushBindingEditModal";
import { Modal } from "@/components/ui/modal";
import { modelSourceLabel } from "@/lib/modelConfig";
import {
  hasRemoteTemplates,
  bindingSkeletonJson,
  requiredBindingKeysFor,
  validateFieldMapping,
  missingBindingKey,
} from "@/adapters/pushProfiles";

// ---------------------------------------------------------------------------
// /push-bindings — 推送站绑定(v6→main 域重构;新)
//
// main_template × push_site 多对多绑定。主键 = (template_id, site_id)。
// 承载「推送模板ID(push_template_id,下游远程模板 id)+ 字段映射(field_mapping,
// JSON)」。远程模板列表可用「拉取」按钮从推送站 /templates 探针获取。
// 列表跨模板汇总自 GET /template-push-sites;增删改用 /main-templates/:id/push-sites。
// ---------------------------------------------------------------------------

const inpCls = "h-9 w-full rounded-md border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring";

export function PushBindingsPage() {
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ["push-bindings"],
    queryFn: () => templatePushSitesApi.listAll(),
    staleTime: 15_000,
  });
  const bindings: TemplatePushSite[] = q.data?.items ?? [];

  const templatesQ = useQuery({ queryKey: ["main-templates"], queryFn: () => mainTemplatesApi.list() });
  const templates: MainTemplate[] = templatesQ.data?.items ?? [];

  const sitesQ = useQuery({ queryKey: ["push-sites"], queryFn: () => pushSitesApi.list() });
  const sites: PushSite[] = sitesQ.data?.items ?? [];

  const protocolsQ = useQuery({ queryKey: ["protocols"], queryFn: () => protocolsApi.list() });
  const protocols: MainProtocol[] = protocolsQ.data?.items ?? [];

  const [pageMsg, setPageMsg] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [templateFilter, setTemplateFilter] = useState("");
  const [siteFilter, setSiteFilter] = useState("");

  // 列表行「编辑绑定」的 modal 目标;null = 关闭。
  const [editTarget, setEditTarget] = useState<TemplatePushSite | null>(null);
  const [editErr, setEditErr] = useState<string | null>(null);

  const [fSiteId, setFSiteId] = useState("");
  const [fTplId, setFTplId] = useState("");
  const [fPushTplId, setFPushTplId] = useState("");
  const [fMapping, setFMapping] = useState("");
  const [fModels, setFModels] = useState<string | null>(null);
  const [fModelMapping, setFModelMapping] = useState<string | null>(null);
  const [createErr, setCreateErr] = useState<string | null>(null);

  // 远程模板探针(选中推送站后可拉取):填充 push_template_id 下拉 + field_mapping 提示。
  const [remoteTemplates, setRemoteTemplates] = useState<PushSiteTemplate[]>([]);
  const [selectedRemoteTemplate, setSelectedRemoteTemplate] = useState<PushSiteTemplate | null>(null);
  const [probeMsg, setProbeMsg] = useState<string | null>(null);

  const enabledSites = useMemo(() => sites.filter((s) => s.enabled), [sites]);
  const filteredBindings = useMemo(
    () => bindings.filter((b) =>
      (!templateFilter || b.template_id === Number(templateFilter)) &&
      (!siteFilter || b.site_id === Number(siteFilter))),
    [bindings, templateFilter, siteFilter],
  );
  const tplName = (id: number) => templates.find((t) => t.id === id)?.name ?? "";
  const tplBy = (id: number) => templates.find((t) => t.id === id);
  const protoById = (id: number) => protocols.find((p) => p.id === id);
  const siteName = (id: number) => sites.find((s) => s.id === id)?.name ?? `#${id}`;
  const siteAdapter = (id: number) => sites.find((s) => s.id === id)?.adapter_type ?? "";
  const siteUrl = (id: number) => sites.find((s) => s.id === id)?.url ?? "";

  // 选中的推送站是否暴露"远程模板列表"能力。true → 显示拉取/推送模板ID 控件;
  // false(如 custom-deepnix-ai)→ 收起这些控件(渠道直接 CarateChannel 全量创建)。
  // doc/011:field_mapping 编辑对所有适配器开放 —— 站点级商户参数统一放绑定行。
  const selectedSiteAdapter = fSiteId ? siteAdapter(Number(fSiteId)) : "";
  const hasRemote = fSiteId ? hasRemoteTemplates(selectedSiteAdapter) : true;
  // 未选中站点时无必填 key;选中后按商家声明提示。单一派生入口 requiredBindingKeysFor,
  // /push-bindings、/push-sites、/distribute 三页共用,不会漂移 (doc/041 §3.2)。
  const selectedRequiredKeys = selectedSiteAdapter ? requiredBindingKeysFor(selectedSiteAdapter) : [];

  const probeMut = useMutation({
    mutationFn: () => pushSitesApi.templates(Number(fSiteId)),
    onSuccess: (r) => {
      setRemoteTemplates(r.templates ?? []);
      setProbeMsg(r.ok ? `拉取到 ${r.templates?.length ?? 0} 个远程模板` : (r.error ?? "拉取失败"));
    },
    onError: (e) => setProbeMsg(e instanceof ApiError ? e.message : "拉取失败"),
  });

  const bindMut = useMutation({
    mutationFn: () =>
      templatePushSitesApi.bind(Number(fTplId), {
        site_id: Number(fSiteId),
        push_template_id: Number(fPushTplId) || 0,
        field_mapping: fMapping.trim(),
        models: fModels,
        model_mapping: fModelMapping,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["push-bindings"] });
      setFSiteId("");
      setFTplId("");
      setFPushTplId("");
      setFMapping("");
      setFModels(null);
      setFModelMapping(null);
      setRemoteTemplates([]);
      setProbeMsg(null);
      setCreateErr(null);
      setCreateOpen(false);
      setPageMsg("已绑定");
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 409) {
        setCreateErr(e.message);
      } else {
        setCreateErr(e instanceof ApiError ? e.message : "绑定失败");
      }
    },
  });

  // 已绑定的 (site_id → template_id) 集合,用于新增绑定前的友好拦截与站点下拉标记。
  const boundSiteTpl = useMemo(() => {
    const m = new Map<number, Set<number>>();
    for (const b of bindings) {
      if (!m.has(b.site_id)) m.set(b.site_id, new Set());
      m.get(b.site_id)!.add(b.template_id);
    }
    return m;
  }, [bindings]);

  const siteBoundTo = (siteId: number): MainTemplate[] =>
    Array.from(boundSiteTpl.get(siteId) ?? [])
      .map((tid) => templates.find((t) => t.id === tid))
      .filter((t): t is MainTemplate => !!t);

  const alreadyBound = !fSiteId || !fTplId ? false : boundSiteTpl.get(Number(fSiteId))?.has(Number(fTplId)) ?? false;

  function submitBind(e: React.FormEvent) {
    e.preventDefault();
    if (!fSiteId || !fTplId) {
      setCreateErr("请选择主模板与推送站");
      return;
    }
    if (alreadyBound) {
      setCreateErr(
        "该推送站已绑定到当前主模板(主键 = 模板×站点,只能绑一次)。如要调整,请在下方绑定列表直接修改「推送模板ID」,或先解绑再重新绑定。",
      );
      return;
    }
    setCreateErr(null);
    // doc/011 + doc/041 §3.4:保存前全量前端校验(JSON 格式 + 必填 key + 白名单/重复)。
    const { errors, warnings } = validateFieldMapping(selectedSiteAdapter, fMapping);
    if (errors.length > 0) {
      setCreateErr(`field_mapping 不通过:${errors.join(";")}`);
      return;
    }
    if (warnings.length > 0) {
      // warnings non-fatal; show them as amber inline below the textarea by
      // extending createErr with a non-blocking variant — but don't block save.
    }
    bindMut.mutate();
  }

  // 选中远程模板只确定 push_template_id(与编辑弹窗一致)。不预填 field_mapping:
  // FieldMappingEditor 是唯一权威编辑入口,预铺空行既违反 doc/042 risk #5,又会
  // 因空 value 被 validateFieldMapping 拦下,导致保存报「必须是 [{key,value},...] 数组」。
  function pickRemoteTemplate(idStr: string) {
    setFPushTplId(idStr);
    setSelectedRemoteTemplate(remoteTemplates.find((t) => String(t.id) === idStr) ?? null);
  }

  const updateMut = useMutation({
    mutationFn: ({ b, body }: { b: TemplatePushSite; body: Parameters<typeof templatePushSitesApi.update>[2] }) =>
      templatePushSitesApi.update(b.template_id, b.site_id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["push-bindings"] });
      setPageMsg("已保存");
      setEditTarget(null);
      setEditErr(null);
    },
    onError: (e) => setEditErr(e instanceof ApiError ? e.message : "保存失败"),
  });

  const unlinkMut = useMutation({
    mutationFn: (b: TemplatePushSite) => templatePushSitesApi.unbind(b.template_id, b.site_id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["push-bindings"] });
      setPageMsg("已解绑");
    },
    onError: (e) => setPageMsg(e instanceof ApiError ? e.message : "解绑失败"),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <h1 className="text-xl font-semibold">推送站绑定</h1>
          <p className="mt-0.5 text-xs text-muted-foreground">
            主模板 × 推送站 的多对多绑定,承载推送模板ID(下游远程模板)与字段映射(field_mapping)。
            远程模板可用「拉取」从推送站获取。
          </p>
        </div>
        <div className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
          <span>
            <strong className="text-foreground">{filteredBindings.length}</strong> / {bindings.length} 条绑定
          </span>
          {q.isError && <Badge variant="warning">加载失败</Badge>}
          <Button size="sm" className="gap-1.5" onClick={() => { setCreateErr(null); setCreateOpen(true); }}>
            <Plus className="h-3.5 w-3.5" /> 新增绑定
          </Button>
        </div>
      </div>

      {pageMsg && (
        <div className="rounded-md border border-border bg-card px-3 py-2 text-xs text-muted-foreground">
          {pageMsg}
          <button type="button" className="ml-2 hover:text-foreground" onClick={() => setPageMsg(null)} aria-label="关闭提示">×</button>
        </div>
      )}

      {createOpen && (
        <Modal
          title="新增绑定"
          subtitle="主模板 × 推送站:选择主模板与推送站,可先「拉取」远程模板,再填写字段映射与模型配置。"
          onClose={() => setCreateOpen(false)}
          defaultSize="lg"
          sizeControls
        >
        {createErr && (
          <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">{createErr}</div>
        )}
        <form className="grid grid-cols-1 gap-3 sm:grid-cols-2" onSubmit={submitBind}>
          <div className="space-y-1">
            <Label>主模板 <span className="text-destructive">*</span></Label>
            <select className={inpCls} value={fTplId} onChange={(e) => setFTplId(e.target.value)}>
              <option value="">— 选择主模板 —</option>
              {templates.filter((t) => t.enabled).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.code} (协议 {t.protocol_type})
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label>推送站 <span className="text-destructive">*</span></Label>
            <div className="flex gap-2">
                <select className={inpCls} value={fSiteId} onChange={(e) => { setFSiteId(e.target.value); setRemoteTemplates([]); setSelectedRemoteTemplate(null); setProbeMsg(null); if (e.target.value && !hasRemoteTemplates(siteAdapter(Number(e.target.value))) && !fMapping.trim()) { const sk = bindingSkeletonJson(siteAdapter(Number(e.target.value))); if (sk) setFMapping(sk); } }}>
                <option value="">— 选择推送站 —</option>
                {enabledSites.map((s) => {
                  const boundTpls = siteBoundTo(s.id);
                  const suffix = boundTpls.length > 0 ? `(已绑 ${boundTpls.map((t) => t.code).join("、")})` : "";
                  return (
                    <option key={s.id} value={s.id}>
                      {s.name} (push · {s.adapter_type}){suffix}
                    </option>
                  );
                })}
              </select>
              {hasRemote && (
                <Button
                  type="button"
                  size="sm"
                  className="shrink-0 gap-1.5 font-semibold"
                  disabled={!fSiteId || probeMut.isPending}
                  onClick={() => probeMut.mutate()}
                  title="拉取该推送站的远程模板列表"
                >
                  <Download className="h-3.5 w-3.5" />
                  {probeMut.isPending ? "拉取中…" : "拉取"}
                </Button>
              )}
            </div>
            {hasRemote && probeMsg && (
              <p className="text-[11px] text-muted-foreground">{probeMsg}</p>
            )}
            {hasRemote ? (
              <p className="text-[11px] text-amber-600">
                选择主模板与推送站后,请先点「<b>拉取</b>」拉取该推送站的远程模板,再保存绑定。
              </p>
            ) : (
              <p className="text-[11px] text-muted-foreground">
                该适配器不需要绑定远程模板,直接填写 field_mapping 保存即可(doc/011)。
              </p>
            )}
            <p className="text-[11px] text-muted-foreground">
              一个推送站可绑定<b>多个</b>主模板;但同一「主模板 × 推送站」只能绑一次。若要给某站换主模板,请先解绑旧绑定。
            </p>
            {alreadyBound && (
              <p className="text-[11px] text-destructive">
                该推送站已绑定到当前主模板,直接保存会失败。请在下方列表修改「推送模板ID」,或先解绑。
              </p>
            )}
          </div>
          {hasRemote && (
            <div className="space-y-1">
              <Label>推送模板ID(远程)</Label>
              {remoteTemplates.length > 0 ? (
                <select className={inpCls} value={fPushTplId} onChange={(e) => pickRemoteTemplate(e.target.value)}>
                  <option value="">— 选择远程模板 —</option>
                  {remoteTemplates.map((rt) => (
                    <option key={rt.id} value={rt.id}>{rt.name} (#{rt.id})</option>
                  ))}
                </select>
              ) : (
                <Input className={inpCls} type="number" value={fPushTplId} onChange={(e) => setFPushTplId(e.target.value)} placeholder="远程模板 id(可先「拉取」)" />
              )}
              {selectedRemoteTemplate && (
                <pre className="mt-1 max-h-48 overflow-auto rounded-md border bg-muted/50 px-3 py-2 font-mono text-[11px] leading-relaxed text-muted-foreground">
                  {JSON.stringify(selectedRemoteTemplate, null, 2)}
                </pre>
              )}
            </div>
          )}
          <div className="space-y-1">
            <Label>
              字段映射 field_mapping(JSON)
              {selectedRequiredKeys.length > 0 && (
                <span className="ml-1 text-amber-600">必填:{selectedRequiredKeys.join("、")}</span>
              )}
              {!hasRemote && selectedSiteAdapter && bindingSkeletonJson(selectedSiteAdapter) && (
                <button
                  type="button"
                  className="ml-1 text-[10px] font-normal text-muted-foreground underline hover:text-foreground"
                  onClick={() => { setFMapping(bindingSkeletonJson(selectedSiteAdapter)!); setCreateErr(null); }}
                  title="填入该适配器的默认字段映射骨架(可在其基础上编辑)"
                >
                  填默认
                </button>
              )}
            </Label>
            <FieldMappingEditor
              adapterType={selectedSiteAdapter}
              value={fMapping}
              onChange={(j) => {
                setFMapping(j);
                setCreateErr(null);
              }}
            />
          </div>
          <div className="sm:col-span-2 rounded-lg border bg-muted/20 p-3">
            <InheritedModelConfigEditor
              models={fModels}
              modelMapping={fModelMapping}
              inheritedModels={(sites.find((s) => s.id === Number(fSiteId))?.models ?? templates.find((t) => t.id === Number(fTplId))?.models) || ""}
              inheritedModelMapping={(sites.find((s) => s.id === Number(fSiteId))?.model_mapping ?? templates.find((t) => t.id === Number(fTplId))?.model_mapping) || ""}
              onModelsChange={setFModels}
              onModelMappingChange={setFModelMapping}
            />
          </div>
          <div className="sm:col-span-2 flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setCreateOpen(false)}>取消</Button>
            <Button type="submit" disabled={bindMut.isPending}>
              {bindMut.isPending ? "保存中…" : "保存绑定"}
            </Button>
          </div>
        </form>
        </Modal>
      )}

      {/* 绑定列表 */}
      <div className="overflow-hidden rounded-xl border bg-card p-3 shadow-sm">
        <div className="mb-2 border-b pb-2.5 text-xs font-medium text-muted-foreground">
          绑定列表 · 主模板 × 推送站
        </div>
        <TableShell
          key={`${templateFilter}|${siteFilter}`}
          mode="client"
          columns={pushBindingColumns}
          data={filteredBindings}
          emptyText={bindings.length === 0 ? "暂无绑定。选择一个主模板 + 推送站开始绑定。" : "没有匹配筛选条件的绑定。"}
          search={{ placeholder: "搜索模板/站点/适配器/URL", match: (b) => `${b.template_name} ${b.site_name} ${siteAdapter(b.site_id)} ${siteUrl(b.site_id)}` }}
          filters={
            <>
              <select className={`${inpCls} sm:w-56`} value={templateFilter} onChange={(e) => setTemplateFilter(e.target.value)} aria-label="筛选主模板">
                <option value="">全部主模板</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.code} · {t.name}</option>)}
              </select>
              <select className={`${inpCls} sm:w-56`} value={siteFilter} onChange={(e) => setSiteFilter(e.target.value)} aria-label="筛选推送站">
                <option value="">全部推送站</option>
                {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </>
          }
          rowKey={(b) => `${b.template_id}/${b.site_id}`}
          renderRow={(b) => {
            const t = tplBy(b.template_id);
            return (
              <PushBindingRow
                b={b}
                templateCode={t?.code ?? ""}
                siteLabel={b.site_name || siteName(b.site_id)}
                templateName={b.template_name || t?.name || ""}
                adapterType={siteAdapter(b.site_id)}
                siteUrl={siteUrl(b.site_id)}
                proto={t ? protoById(t.main_protocol_id) : undefined}
                showRemoteTemplateCells={hasRemoteTemplates(siteAdapter(b.site_id))}
                onEdit={() => { setEditTarget(b); setEditErr(null); }}
                onUnlink={() => {
                  if (confirm(`确认解绑「${b.template_name || tplName(b.template_id)} × ${b.site_name || siteName(b.site_id)}」?`)) {
                    unlinkMut.mutate(b);
                  }
                }}
              />
            );
          }}
        />
      </div>

      {editTarget && (
        <PushBindingEditModal
          binding={editTarget}
          site={sites.find((s) => s.id === editTarget.site_id)}
          template={tplBy(editTarget.template_id)}
          adapterType={siteAdapter(editTarget.site_id)}
          showRemoteTemplate={hasRemoteTemplates(siteAdapter(editTarget.site_id))}
          saving={updateMut.isPending}
          error={editErr}
          onClose={() => { setEditTarget(null); setEditErr(null); }}
          onSave={(body) => updateMut.mutate({ b: editTarget, body })}
        />
      )}
    </div>
  );
}

const pushBindingColumns: ColumnSpec<TemplatePushSite>[] = [
  { key: "protocol_type", label: "协议" },
  { key: "template_name", label: "主模板", sortable: true },
  { key: "site_name", label: "推送站", sortable: true },
  { key: "push_template_id", label: "推送模板ID" },
  { key: "field_mapping", label: "字段映射" },
  { key: "models", label: "模型配置" },
  { key: "actions", label: "操作", align: "center" },
];

// fieldMappingCount returns the number of entries in the binding's field_mapping JSON
// (tolerant of invalid JSON). Used by the row preview cell.
function fieldMappingCount(json: string): number {
  try {
    const p = JSON.parse(json || "");
    if (Array.isArray(p)) return p.filter((e: { key?: unknown }) => !!e?.key).length;
  } catch {}
  return 0;
}

function PushBindingRow({
  b,
  templateCode,
  siteLabel,
  templateName,
  adapterType,
  siteUrl,
  proto,
  showRemoteTemplateCells,
  onEdit,
  onUnlink,
}: {
  b: TemplatePushSite;
  templateCode: string;
  siteLabel: string;
  templateName: string;
  adapterType: string;
  siteUrl: string;
  proto: MainProtocol | undefined;
  showRemoteTemplateCells: boolean;
  onEdit: () => void;
  onUnlink: () => void;
}) {
  const count = fieldMappingCount(b.field_mapping ?? "");
  const miss = missingBindingKey(adapterType, b.field_mapping ?? "");

  return (
    <tr className="border-b hover:bg-muted/30">
      <td className="px-3 py-2.5">
        <Badge variant="muted">{proto?.code ?? "—"}</Badge>
        {proto?.name && <div className="mt-0.5 text-[11px] text-muted-foreground">{proto.name}</div>}
      </td>
      <td className="px-3 py-2.5">
        <div className="font-mono text-xs">{templateCode || `#${b.template_id}`}</div>
        {templateName && <div className="text-[11px] text-muted-foreground">{templateName}</div>}
      </td>
      <td className="px-3 py-2.5">
        <div className="font-medium">{siteLabel}</div>
        <div className="text-[11px] text-muted-foreground">{adapterType || "push"}</div>
        {siteUrl ? (
          <a
            href={siteUrl}
            target="_blank"
            rel="noreferrer"
            className="block max-w-56 truncate text-[11px] text-primary hover:underline"
            title={siteUrl}
          >
            {siteUrl}
          </a>
        ) : (
          <div className="text-[11px] text-muted-foreground">—</div>
        )}
      </td>
      <td className="px-3 py-2.5">
        {showRemoteTemplateCells ? (
          <span className="text-xs tabular-nums">{b.push_template_id || "—"}</span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-3 py-2.5">
        <div className="text-[11px] text-muted-foreground">
          {count} 个字段
          {miss ? (
            <span className="ml-1 font-medium text-destructive">缺 {miss}</span>
          ) : count > 0 ? (
            <span className="ml-1 font-medium text-emerald-600">✓</span>
          ) : null}
        </div>
      </td>
      <td className="px-3 py-2.5">
        <div className="max-w-40 truncate text-xs" title={b.effective_models}>{b.effective_models || "未设置"}</div>
        <div className="text-[11px] text-muted-foreground">{modelSourceLabel(b.models_source)}</div>
      </td>
      <td className="px-3 py-2.5 text-center">
        <div className="flex items-center justify-center gap-1">
          <button
            type="button"
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
            onClick={onEdit}
            title="编辑绑定"
            aria-label="编辑绑定"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            onClick={onUnlink}
            title="解绑"
            aria-label="解绑"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </td>
    </tr>
  );
}
